import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import ScheduleAPI from '../api/ScheduleAPI'
import { SCHEDULE_QUERY } from '../api/ScheduleAPI.model'
import {
  shiftsInWeek,
  weekDtoToStore,
  weekStartOf
} from '../api/scheduleMapper'
import { SCHEDULE_USE_API } from '../config'
import { useScheduleStore } from '../store/useScheduleStore'
import { Shift } from '../types/schedule.model'
import { dayjs, shiftDurationHours, toDateKey } from '../utils/schedule'

/** Rostered hours per employee, keyed by user id. */
export type HoursByUserId = Map<string, number>

export interface WeeklyRosteredHours {
  /** Hours rostered in the current ISO week. */
  currentWeek: HoursByUserId
  /** The same for the week before, what a week-on-week trend compares to. */
  previousWeek: HoursByUserId
  isLoading: boolean
}

const sumByEmployee = (shifts: Shift[]): HoursByUserId => {
  const hours: HoursByUserId = new Map()

  shifts.forEach(shift => {
    hours.set(
      shift.userId,
      (hours.get(shift.userId) ?? 0) + shiftDurationHours(shift)
    )
  })

  // Both consumers print the number as it comes, and adding quarter hours as
  // floats lands on 37.499999999999996 often enough to be worth a pass.
  hours.forEach((total, userId) =>
    hours.set(userId, Math.round(total * 10) / 10)
  )

  return hours
}

const useWeekHours = (
  weekStart: string
): { hours: HoursByUserId; isLoading: boolean } => {
  const storeShifts = useScheduleStore(state => state.shifts)

  const { data, isLoading } = useQuery({
    // The same key the schedule screen uses, so having both open fetches the
    // week once.
    queryKey: [
      SCHEDULE_QUERY.WEEK,
      localStorage.getItem('restaurantId'),
      weekStart
    ],
    queryFn: () => ScheduleAPI.getWeek(weekStart),
    enabled: SCHEDULE_USE_API
  })

  const hours = useMemo(() => {
    // Read-only on purpose: this hook is mounted outside the schedule screen,
    // and hydrating the store from here would overwrite roster edits the user
    // has not saved yet.
    const apiShifts = data ? weekDtoToStore(data).shifts : []
    const shifts = SCHEDULE_USE_API
      ? apiShifts
      : shiftsInWeek(storeShifts, weekStart)

    return sumByEmployee(shifts)
  }, [data, storeShifts, weekStart])

  return { hours, isLoading: SCHEDULE_USE_API && isLoading }
}

/**
 * Hours each employee is rostered for this week and last, for screens that
 * report on the roster without editing it.
 *
 * Reads the shifts API when it is live and the local store otherwise, which is
 * the same seam every other consumer of the schedule sits behind, see
 * `config.ts`.
 */
export const useWeeklyRosteredHours = (): WeeklyRosteredHours => {
  // Derived from today rather than captured once, so a tab left open over
  // Sunday midnight stops reporting the week that just ended.
  const today = toDateKey(dayjs())
  const currentWeekStart = useMemo(() => weekStartOf(today), [today])
  const previousWeekStart = useMemo(
    () => toDateKey(dayjs(currentWeekStart).subtract(7, 'day')),
    [currentWeekStart]
  )

  const current = useWeekHours(currentWeekStart)
  const previous = useWeekHours(previousWeekStart)

  return {
    currentWeek: current.hours,
    previousWeek: previous.hours,
    isLoading: current.isLoading || previous.isLoading
  }
}
